import {
    McpAuthType,
    McpResource,
    McpResourceCsp,
    McpResourceMeta,
    McpResourcePreferredSize,
    McpResourceProcessingStatus,
    McpServerType,
    McpTool,
    McpToolMetaCompatibilityMode,
    McpToolVisibility,
} from 'client/model/aliceAgentApp';
import { UserAgreementOrDraft } from 'client/model/userAgreement';

import { LocalizedString } from '../AliceAgentAppPage/LocalizedTextInputs';

/**
 * Stable identifier generated client-side at the moment a tool/resource is added to the form
 * (or hydrated from the manifest). Used as React `key` so that removing item N doesn't
 * shift the keys of items N+1, N+2… — index-based keys produced state-loss bugs when one of the
 * inner fields was focused at the time of removal.
 */
const generateFormId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export interface ToolFormState {
    /** Client-only id for stable React keys; never sent to the server. */
    id: string;
    name: string;
    title: LocalizedString;
    description: LocalizedString;
    inputSchemaText: string;
    annotations: {
        readOnlyHint: boolean;
        destructiveHint: boolean;
        idempotentHint: boolean;
        openWorldHint: boolean;
    };
    visibility: McpToolVisibility;
    metaCompatibilityMode: McpToolMetaCompatibilityMode;
    outputTemplate: string;
    invocation: {
        invoking: LocalizedString;
        invoked: LocalizedString;
    };
}

/**
 * Single CSP domain row in the form. Carries a client-side `id` so the editor can use a stable
 * React key — removing item N would otherwise shift indices and React would reuse the wrong
 * DOM nodes (focus jumps, partially-typed input lands on a neighbor).
 */
interface DomainEntryFormState {
    id: string;
    value: string;
}

export const emptyDomainEntry = (value = ''): DomainEntryFormState => ({
    id: generateFormId(),
    value,
});

/**
 * Per-field form state for a resource's `meta` block. Numeric preferred-size values are kept
 * as strings so the user can type freely (empty, intermediate values like "1") — converted to
 * numbers only when serialising back to the manifest.
 */
export interface ResourceMetaFormState {
    prefersBorder: boolean;
    domain: string;
    cspConnectDomains: DomainEntryFormState[];
    cspResourceDomains: DomainEntryFormState[];
    minHeight: string;
    maxHeight: string;
    minWidth: string;
    maxWidth: string;
}

export interface ResourceFormState {
    /** Client-only id for stable React keys; never sent to the server. */
    id: string;
    uri: string;
    internalUri: string;
    mimeType: string;
    name: string;
    description: string;
    meta: ResourceMetaFormState;
    /** Display-only; the server is the only writer. */
    processingStatus?: McpResourceProcessingStatus;
    processingError?: string;
}

export interface ExternalFormState {
    name: string;
    logoId?: string;
    logoUrl?: string;
    useZora: boolean;
    title: LocalizedString;
    shortDescription: LocalizedString;
    prompt: LocalizedString;
    mcp: {
        endpoint: string;
        serverType: McpServerType;
        authType: McpAuthType;
        apiKey: string;
    };
    /**
     * Mirrors `AliceAgentAppManifest.isAuthorizationRequired`. When `true`, publish is
     * gated server-side until an OAuth-app is attached to the skill ("Связка аккаунтов" tab).
     */
    isAuthorizationRequired: boolean;
    tools: ToolFormState[];
    resources: ResourceFormState[];
    chatTestingEnabled: boolean;
    /** User agreements live in form state but are persisted via their own CRUD endpoints, not the manifest. */
    userAgreements: UserAgreementOrDraft[];
}

export const emptyTool = (): ToolFormState => ({
    id: generateFormId(),
    name: '',
    title: { ru: '', en: '' },
    description: { ru: '', en: '' },
    inputSchemaText: '{}',
    annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
    },
    visibility: 'public',
    metaCompatibilityMode: 'unspecified',
    outputTemplate: '',
    invocation: {
        invoking: { ru: '', en: '' },
        invoked: { ru: '', en: '' },
    },
});

/** Common HTML widget MIME types (dev-console dropdown). Any `text/html+*` is accepted on save. */
export const SUPPORTED_RESOURCE_MIME_TYPES = ['text/html', 'text/html+appbridge', 'text/html+skybridge'] as const;

export const isSupportedResourceMimeType = (mimeType: string): boolean => {
    const normalized = mimeType.trim().toLowerCase().split(';')[0].trim();
    return normalized === 'text/html' || normalized.startsWith('text/html+');
};

export const emptyResource = (): ResourceFormState => ({
    id: generateFormId(),
    uri: '',
    internalUri: '',
    mimeType: 'text/html+appbridge',
    name: '',
    description: '',
    meta: emptyResourceMeta(),
});

const emptyResourceMeta = (): ResourceMetaFormState => ({
    prefersBorder: false,
    domain: '',
    cspConnectDomains: [],
    cspResourceDomains: [],
    minHeight: '',
    maxHeight: '',
    minWidth: '',
    maxWidth: '',
});

const numberToString = (n: number | null | undefined): string => (n === null || n === undefined ? '' : String(n));

const parseNumberOrNull = (s: string): number | null => {
    const trimmed = s.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
};

const tryParseJson = (text: string): unknown => {
    if (!text.trim()) return undefined;
    try {
        return JSON.parse(text);
    } catch {
        return undefined;
    }
};

const stringifyJson = (value: unknown, fallback: string): string => {
    if (value === undefined || value === null) return fallback;
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return fallback;
    }
};

/**
 * MCP servers commonly hand back `title`/`description` as a plain string rather than a
 * locale map (the MCP spec doesn't say one way or the other). Treat that case as "same value
 * for every locale" so the user sees the imported text in both ru and en fields right away.
 */
const fromLocalizedOrString = (value: unknown): LocalizedString => {
    if (typeof value === 'string') {
        return { ru: value, en: value };
    }
    if (value && typeof value === 'object') {
        const map = value as Record<string, string>;
        return { ru: map.ru ?? '', en: map.en ?? '' };
    }
    return { ru: '', en: '' };
};

const toLocalized = (value: LocalizedString): Record<string, string> => {
    const out: Record<string, string> = {};
    if (value.ru) out.ru = value.ru;
    if (value.en) out.en = value.en;
    return out;
};

export const toolFromManifest = (tool: McpTool): ToolFormState => ({
    id: generateFormId(),
    name: tool.name,
    title: fromLocalizedOrString(tool.title),
    description: fromLocalizedOrString(tool.description),
    inputSchemaText: stringifyJson(tool.inputSchema, '{}'),
    annotations: {
        readOnlyHint: tool.annotations?.readOnlyHint ?? false,
        destructiveHint: tool.annotations?.destructiveHint ?? false,
        idempotentHint: tool.annotations?.idempotentHint ?? false,
        openWorldHint: tool.annotations?.openWorldHint ?? false,
    },
    visibility: tool.meta?.visibility ?? 'public',
    metaCompatibilityMode: tool.meta?.metaCompatibilityMode ?? 'unspecified',
    outputTemplate: tool.meta?.outputTemplate ?? '',
    invocation: {
        invoking: fromLocalizedOrString(tool.meta?.toolInvocation?.invoking),
        invoked: fromLocalizedOrString(tool.meta?.toolInvocation?.invoked),
    },
});

export const toolToManifest = (tool: ToolFormState): McpTool => ({
    name: tool.name,
    title: toLocalized(tool.title),
    description: toLocalized(tool.description),
    inputSchema: tryParseJson(tool.inputSchemaText) ?? {},
    annotations: tool.annotations,
    meta: {
        outputTemplate: tool.outputTemplate || null,
        visibility: tool.visibility,
        metaCompatibilityMode: tool.metaCompatibilityMode,
        toolInvocation: {
            invoking: toLocalized(tool.invocation.invoking),
            invoked: toLocalized(tool.invocation.invoked),
        },
    },
});

export const resourceFromManifest = (resource: McpResource): ResourceFormState => ({
    id: generateFormId(),
    uri: resource.uri,
    internalUri: resource.internalUri ?? '',
    mimeType: resource.mimeType,
    name: resource.name ?? '',
    description: resource.description ?? '',
    meta: resourceMetaFromManifest(resource.meta),
    processingStatus: resource.processingStatus,
    processingError: resource.processingError ?? undefined,
});

const resourceMetaFromManifest = (meta: McpResourceMeta | undefined): ResourceMetaFormState => ({
    prefersBorder: meta?.prefersBorder ?? false,
    domain: meta?.domain ?? '',
    cspConnectDomains: (meta?.csp?.connectDomains ?? []).map((v) => emptyDomainEntry(v)),
    cspResourceDomains: (meta?.csp?.resourceDomains ?? []).map((v) => emptyDomainEntry(v)),
    minHeight: numberToString(meta?.preferredSize?.minHeight),
    maxHeight: numberToString(meta?.preferredSize?.maxHeight),
    minWidth: numberToString(meta?.preferredSize?.minWidth),
    maxWidth: numberToString(meta?.preferredSize?.maxWidth),
});

export const resourceToManifest = (resource: ResourceFormState): McpResource => ({
    uri: resource.uri,
    internalUri: resource.internalUri || null,
    mimeType: resource.mimeType,
    name: resource.name || null,
    description: resource.description || null,
    meta: resourceMetaToManifest(resource.meta),
});

const resourceMetaToManifest = (meta: ResourceMetaFormState): McpResourceMeta => {
    const connectDomains = meta.cspConnectDomains.map((d) => d.value.trim()).filter(Boolean);
    const resourceDomains = meta.cspResourceDomains.map((d) => d.value.trim()).filter(Boolean);
    const csp: McpResourceCsp | null =
        connectDomains.length > 0 || resourceDomains.length > 0 ? { connectDomains, resourceDomains } : null;
    const preferredSize: McpResourcePreferredSize | null = (() => {
        const minH = parseNumberOrNull(meta.minHeight);
        const maxH = parseNumberOrNull(meta.maxHeight);
        const minW = parseNumberOrNull(meta.minWidth);
        const maxW = parseNumberOrNull(meta.maxWidth);
        if (minH === null && maxH === null && minW === null && maxW === null) return null;
        return { minHeight: minH, maxHeight: maxH, minWidth: minW, maxWidth: maxW };
    })();
    return {
        prefersBorder: meta.prefersBorder,
        domain: meta.domain.trim() || null,
        csp,
        preferredSize,
    };
};

/**
 * Server-side validation error key → form field path. Used to attach errors from the backend
 * (DevConsoleBadRequestException) onto the right inputs after a failed save.
 */
export const externalServerKeyMap: Record<string, string> = {
    name: 'name',
    logoId: 'logoId',
    'manifest.config.mcp.endpoint': 'mcp.endpoint',
    'manifest.config.mcp': 'mcp.endpoint',
};

/**
 * Validator helper for JSON-typed form fields (inputSchema, resource meta). Returns the i18n
 * key for the error message, or undefined when valid. Empty string is treated as "no value"
 * and accepted — the field is optional.
 */
export const isValidJsonOrEmpty = (text: string): boolean => {
    const trimmed = text.trim();
    if (!trimmed) return true;
    try {
        JSON.parse(trimmed);
        return true;
    } catch {
        return false;
    }
};
